import { useState, useEffect } from "react";
import logo from "./log_webadd.png";
import homeSheela from "./home_sheela.png";

const LOGO_SRC = logo;

const FOUNDER_MESSAGE = {
  name: "Rajbhar Sheela",
  title: "Founder, WebAdd",
  photo: homeSheela,
  quote:
    "We started WebAdd to make getting online simple — from your very first sign-up to the systems that keep your business running behind the scenes. Every feature we build starts with one question: does this make things easier for the person using it. That's the standard we hold ourselves to, and it isn't going to change as we grow.",
};

const NAV_ITEMS = ["Home", "About", "Services", "Pricing", "Our Work", "Reviews", "Contact Us", "Connect"];

// Maps each nav item to a real URL path so navigation is an actual redirect
// (updates the address bar, supports back/forward) instead of a popup.
const SECTION_PATHS = {
  Home: "/",
  About: "/about",
  Services: "/services",
  Pricing: "/pricing",
  "Our Work": "/our-work",
  Reviews: "/reviews",
  "Contact Us": "/contact",
  Connect: "/connect",
};
const PATH_TO_SECTION = Object.fromEntries(
  Object.entries(SECTION_PATHS).map(([section, path]) => [path, section])
);

const INDIAN_STATES = [
  "Andhra Pradesh", "Arunachal Pradesh", "Assam", "Bihar", "Chhattisgarh", "Goa", "Gujarat",
  "Haryana", "Himachal Pradesh", "Jharkhand", "Karnataka", "Kerala", "Madhya Pradesh",
  "Maharashtra", "Manipur", "Meghalaya", "Mizoram", "Nagaland", "Odisha", "Punjab",
  "Rajasthan", "Sikkim", "Tamil Nadu", "Telangana", "Tripura", "Uttar Pradesh",
  "Uttarakhand", "West Bengal", "Delhi", "Jammu and Kashmir", "Ladakh", "Puducherry",
  "Chandigarh", "Andaman and Nicobar Islands",
];

const LANGUAGES = ["English", "Hindi", "Bengali", "Tamil", "Telugu", "Marathi", "Gujarati", "Kannada", "Malayalam", "Punjabi", "Urdu"];

const COMMUNICATION_MODES = ["SMS", "Email", "WhatsApp", "Call"];

const SECURITY_QUESTIONS = [
  "What is your pet's name?",
  "What is your mother's maiden name?",
  "What was the name of your first school?",
  "What is your favorite book?",
  "What city were you born in?",
];

// Placeholder content shown in the nav modal for each section.
// Swap these in with your real copy whenever it's ready.
const SECTION_CONTENT = {
  Home: {
    title: "A message from our founder",
    founder: FOUNDER_MESSAGE,
  },
  About: {
    title: "About WebAdd",
    paragraphs: [
      "WebAdd is a digital services studio that helps individuals and small businesses get set up online — quickly, affordably, and without the technical headache.",
      "From website design to secure onboarding and communication tools, we build the pieces that let you focus on running your business instead of managing your tech stack.",
    ],
  },
  Services: {
    title: "What we do",
    paragraphs: ["We offer a focused set of services designed to get you online and keep you running smoothly:"],
    list: [
      "Website design & development",
      "SMS, email & WhatsApp notifications (OTP, alerts, marketing)",
      "Secure sign-up & identity verification flows",
      "Hosting, domain setup & maintenance",
      "Ongoing support & digital marketing",
    ],
  },
  Pricing: {
    title: "Pricing",
    paragraphs: ["Every project is a little different, so we tailor pricing to what you actually need. As a starting point:"],
    list: [
      "Starter — a single-page site with the essentials",
      "Growth — a multi-page site with sign-up, OTP, and forms",
      "Enterprise — a fully custom platform with ongoing support",
    ],
    footnote: "Reach out and we'll put together a quote based on your requirements.",
  },
  "Our Work": {
    title: "Our work",
    paragraphs: [
      "We're putting together a showcase of recent projects — check back soon, or get in touch and we'll walk you through examples relevant to what you're building.",
    ],
  },
  Reviews: {
    title: "What people say",
    quotes: [
      { text: "They took our idea and had a working site live faster than we expected.", author: "Early client" },
      { text: "Straightforward process, clear communication the whole way through.", author: "Early client" },
    ],
  },
  "Contact Us": {
    title: "Contact us",
    paragraphs: ["Have a question or ready to get started? Reach out any way that's convenient:"],
    list: ["Email: webadd2015@gmail.com", "Phone: +91 9140935411"],
    footnote: "Or send us a message directly and we'll get back to you.",
    cta: "connect",
  },
  Connect: {
    title: "Let's connect",
    paragraphs: ["Tell us a bit about what you need and we'll get back to you shortly."],
    isConnectForm: true,
  },
};

export default function ComingSoon() {
  const [email, setEmail] = useState("");
  const [submitted, setSubmitted] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState("");
  const [risen, setRisen] = useState(false);
  const [search, setSearch] = useState("");
  const [menuOpen, setMenuOpen] = useState(false);

  // --- Sign up modal state ---
  const [showSignup, setShowSignup] = useState(false);
  const [signupSending, setSignupSending] = useState(false);
  const [signupError, setSignupError] = useState("");
  const [signupDone, setSignupDone] = useState(false);

  // --- Sign in modal state ---
  const [showSignin, setShowSignin] = useState(false);
  const [signinStep, setSigninStep] = useState("credentials"); // "credentials" | "otp" | "done"
  const [signinUsername, setSigninUsername] = useState("");
  const [signinMobile, setSigninMobile] = useState("");
  const [signinPassword, setSigninPassword] = useState("");
  const [signinOtp, setSigninOtp] = useState("");
  const [signinSending, setSigninSending] = useState(false);
  const [signinError, setSigninError] = useState("");
  const [otpMaskedContact, setOtpMaskedContact] = useState("");
  const [resendCooldown, setResendCooldown] = useState(0);

  // --- Page navigation state (About, Services, Pricing, Our Work, Reviews, Contact Us, Connect) ---
  // Reflects (and drives) the real URL so nav clicks are actual redirects, not popups.
  const [activeSection, setActiveSection] = useState(() => {
    if (typeof window === "undefined") return "Home";
    return PATH_TO_SECTION[window.location.pathname] || "Home";
  });

  // --- Connect form state (rendered as its own page at /connect) ---
  const [connectName, setConnectName] = useState("");
  const [connectEmail, setConnectEmail] = useState("");
  const [connectMobile, setConnectMobile] = useState("");
  const [connectMessage, setConnectMessage] = useState("");
  const [connectSending, setConnectSending] = useState(false);
  const [connectError, setConnectError] = useState("");
  const [connectDone, setConnectDone] = useState(false);

  const emptyFormData = {
    firstName: "",
    lastName: "",
    parentName: "",
    dob: "",
    gender: "Male",
    mobile: "",
    email: "",
    altMobile: "",
    house: "",
    street: "",
    city: "",
    district: "",
    state: "",
    pin: "",
    country: "India",
    aadhaar: "",
    pan: "",
    profilePhoto: null,
    identityProof: null,
    username: "",
    password: "",
    confirmPassword: "",
    securityQuestion: "",
    securityAnswer: "",
    preferredLanguage: "English",
    preferredCommunication: "SMS",
    termsAgreed: false,
    privacyAgreed: false,
    captchaChecked: false,
  };

  const [formData, setFormData] = useState(emptyFormData);

  const handleFieldChange = (e) => {
    const { name, value, type, checked, files } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : type === "file" ? files?.[0] || null : value,
    }));
  };

  // Base URL of the backend API (see server.js) that connects to SQL Server.
  // Left empty on purpose: with "proxy": "http://localhost:5000" set in this
  // app's package.json, requests to /api/... are forwarded to the backend
  // automatically during `npm start` — no CORS setup needed in development.
  // In production, set this to your deployed API's URL, e.g.
  // "https://api.yourdomain.com".
  const API_BASE_URL = "";

  useEffect(() => {
    const t = setTimeout(() => setRisen(true), 200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const onPopState = () => {
      setActiveSection(PATH_TO_SECTION[window.location.pathname] || "Home");
    };
    window.addEventListener("popstate", onPopState);
    return () => window.removeEventListener("popstate", onPopState);
  }, []);

  useEffect(() => {
    if (resendCooldown <= 0) return;
    const t = setTimeout(() => setResendCooldown((s) => s - 1), 1000);
    return () => clearTimeout(t);
  }, [resendCooldown]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) return;
    setError("");
    setSending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError(data.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setError("Couldn't reach the server. Please try again.");
    } finally {
      setSending(false);
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    // no-op placeholder: hook up to real search when available
  };

  const closeSignup = () => {
    setShowSignup(false);
    setSignupError("");
    setSignupDone(false);
    setFormData(emptyFormData);
  };

  const handleSignup = async (e) => {
    e.preventDefault();
    setSignupError("");

    if (
      !formData.firstName.trim() ||
      !formData.lastName.trim() ||
      !formData.mobile.trim() ||
      !formData.email.trim() ||
      !formData.username.trim() ||
      !formData.password.trim()
    ) {
      setSignupError("Please fill in all required fields.");
      return;
    }
    if (formData.password !== formData.confirmPassword) {
      setSignupError("Passwords do not match.");
      return;
    }
    if (!formData.termsAgreed || !formData.privacyAgreed) {
      setSignupError("Please accept the Terms and Privacy Policy to continue.");
      return;
    }
    if (!formData.captchaChecked) {
      setSignupError("Please confirm you're not a robot.");
      return;
    }

    setSignupSending(true);
    try {
      // Sends the form to your own backend (see server.js), which writes
      // the record into SQL Server (DESKTOP-UE29GEU → WebAdd database).
      // File fields (profilePhoto, identityProof) are sent as multipart form data;
      // the backend saves them to disk/blob storage and stores the path in the DB.
      const payload = new FormData();
      Object.entries(formData).forEach(([key, value]) => {
        if (value === null || value === undefined) return;
        if (key === "profilePhoto" || key === "identityProof") {
          if (value) payload.append(key, value);
        } else {
          payload.append(key, value);
        }
      });

      const res = await fetch(`${API_BASE_URL}/api/signup`, {
        method: "POST",
        body: payload,
      });

      if (res.ok) {
        setSignupDone(true);
      } else {
        const text = await res.text();
        setSignupError(text || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setSignupError("Couldn't reach the server. Please try again.");
    } finally {
      setSignupSending(false);
    }
  };

  const openSignin = () => {
    setShowSignup(false);
    setShowSignin(true);
  };

  const closeSignin = () => {
    setShowSignin(false);
    setSigninStep("credentials");
    setSigninUsername("");
    setSigninMobile("");
    setSigninPassword("");
    setSigninOtp("");
    setSigninError("");
    setResendCooldown(0);
  };

  // Step 1: verify username-or-mobile + password, backend generates & sends an OTP.
  const handleSigninCredentials = async (e) => {
    e.preventDefault();
    setSigninError("");
    if ((!signinUsername.trim() && !signinMobile.trim()) || !signinPassword.trim()) {
      setSigninError("Please enter your username or mobile number, and your password.");
      return;
    }
    setSigninSending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: signinUsername || null,
          mobileNumber: signinMobile || null,
          password: signinPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSigninUsername(data.username || signinUsername); // backend resolves this if user logged in via mobile
        setOtpMaskedContact(data.maskedContact || "your registered contact");
        setSigninStep("otp");
        setResendCooldown(30);
      } else {
        setSigninError(data.message || "Invalid username/mobile number or password.");
      }
    } catch (err) {
      setSigninError("Couldn't reach the server. Please try again.");
    } finally {
      setSigninSending(false);
    }
  };

  // Step 2: verify the OTP the user received.
  const handleVerifyOtp = async (e) => {
    e.preventDefault();
    setSigninError("");
    if (!signinOtp.trim()) {
      setSigninError("Please enter the OTP sent to you.");
      return;
    }
    setSigninSending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/verify-otp`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ username: signinUsername, otp: signinOtp }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setSigninStep("done");
      } else {
        setSigninError(data.message || "That OTP didn't match. Please try again.");
      }
    } catch (err) {
      setSigninError("Couldn't reach the server. Please try again.");
    } finally {
      setSigninSending(false);
    }
  };

  const handleResendOtp = async () => {
    if (resendCooldown > 0 || signinSending) return;
    setSigninError("");
    setSigninSending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/login`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          username: signinUsername || null,
          mobileNumber: signinMobile || null,
          password: signinPassword,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setResendCooldown(30);
      } else {
        setSigninError(data.message || "Couldn't resend the OTP right now.");
      }
    } catch (err) {
      setSigninError("Couldn't reach the server. Please try again.");
    } finally {
      setSigninSending(false);
    }
  };

  // --- Connect form handlers ---
  const handleConnectSubmit = async (e) => {
    e.preventDefault();
    setConnectError("");
    if (!connectName.trim() || !connectEmail.trim() || !connectMessage.trim()) {
      setConnectError("Please fill in your name, email, and message.");
      return;
    }
    setConnectSending(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/connect`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: connectName,
          email: connectEmail,
          mobile: connectMobile,
          message: connectMessage,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (res.ok) {
        setConnectDone(true);
      } else {
        setConnectError(data.message || "Something went wrong. Please try again.");
      }
    } catch (err) {
      setConnectError("Couldn't reach the server. Please try again.");
    } finally {
      setConnectSending(false);
    }
  };

  // Navigates to a real URL for the given nav item — updates the address bar
  // and browser history instead of opening a popup.
  const navigateTo = (item) => {
    const path = SECTION_PATHS[item] || "/";
    if (typeof window !== "undefined" && window.location.pathname !== path) {
      window.history.pushState({}, "", path);
    }
    setActiveSection(item);
    setMenuOpen(false);
    if (typeof window !== "undefined") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    }
  };

  const handleNavItemClick = (item) => (e) => {
    e.preventDefault();
    navigateTo(item);
  };

  return (
    <div
      style={{
        position: "relative",
        width: "100%",
        minHeight: "100vh",
        background: "linear-gradient(160deg, #0E1712 0%, #17241D 45%, #1E2B24 100%)",
        overflow: "hidden",
        fontFamily: "'Space Grotesk', sans-serif",
        display: "flex",
        flexDirection: "column",
      }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Fraunces:ital,wght@0,300;0,500;1,400&family=Space+Grotesk:wght@300;400;500&display=swap');

        .cs-input:focus {
          outline: 2px solid #8E7CC3;
          outline-offset: 2px;
        }
        .cs-btn {
          transition: transform 0.2s ease, background 0.2s ease;
        }
        .cs-btn:hover {
          transform: translateY(-1px);
          background: #6FDB94;
        }
        .cs-nav-link {
          position: relative;
          color: #D8E3DC;
          text-decoration: none;
          font-size: 14.5px;
          font-weight: 400;
          letter-spacing: 0.01em;
          padding: 4px 2px;
          transition: color 0.2s ease;
          white-space: nowrap;
        }
        .cs-nav-link::after {
          content: "";
          position: absolute;
          left: 0;
          bottom: -2px;
          width: 0%;
          height: 1px;
          background: #6FDB94;
          transition: width 0.25s ease;
        }
        .cs-nav-link:hover {
          color: #FFFFFF;
        }
        .cs-nav-link:hover::after {
          width: 100%;
        }
        .cs-signin-link {
          color: #D8E3DC;
          text-decoration: none;
          font-size: 14.5px;
          white-space: nowrap;
        }
        .cs-signin-link:hover {
          color: #FFFFFF;
        }
        .cs-cta-btn {
          background: #50C878;
          color: #0B1026;
          border: none;
          border-radius: 999px;
          padding: 11px 22px;
          font-family: inherit;
          font-size: 14.5px;
          font-weight: 500;
          cursor: pointer;
          white-space: nowrap;
          transition: background 0.2s ease, transform 0.2s ease;
        }
        .cs-cta-btn:hover {
          background: #6FDB94;
          transform: translateY(-1px);
        }
        .cs-search-form {
          display: flex;
          align-items: center;
          background: rgba(255,255,255,0.08);
          border: 1px solid rgba(255,255,255,0.18);
          border-radius: 999px;
          padding: 4px 6px 4px 14px;
        }
        .cs-search-input {
          border: none;
          background: transparent;
          outline: none;
          font-family: inherit;
          font-size: 13px;
          color: #F4F7F5;
          width: 130px;
        }
        .cs-search-input::placeholder {
          color: rgba(244,247,245,0.5);
        }
        .cs-search-btn {
          border: none;
          background: #50C878;
          color: #0B1026;
          border-radius: 999px;
          width: 28px;
          height: 28px;
          display: flex;
          align-items: center;
          justify-content: center;
          cursor: pointer;
          flex-shrink: 0;
        }
        .cs-burger {
          display: none;
        }
        @media (max-width: 980px) {
          .cs-nav-links { display: none; }
          .cs-search-form { display: none; }
          .cs-signin-link { display: none; }
          .cs-burger { display: flex; }
        }
        @media (prefers-reduced-motion: reduce) {
          .cs-horizon, .cs-star { animation: none !important; }
        }

        .cs-form-grid {
          display: grid;
          grid-template-columns: 1fr 1fr;
          gap: 12px;
        }
        @media (max-width: 560px) {
          .cs-form-grid { grid-template-columns: 1fr; }
        }
        .cs-field {
          display: flex;
          flex-direction: column;
          gap: 5px;
        }
        .cs-field.cs-field-full {
          grid-column: 1 / -1;
        }
        .cs-field label {
          font-size: 12px;
          color: #5E7368;
          letter-spacing: 0.02em;
        }
        .cs-field input,
        .cs-field select,
        .cs-field textarea {
          background: rgba(30,43,36,0.04);
          border: 1px solid rgba(30,43,36,0.15);
          border-radius: 10px;
          padding: 10px 14px;
          color: #1E2B24;
          font-size: 14px;
          font-family: inherit;
          width: 100%;
          box-sizing: border-box;
        }
        .cs-field input:focus,
        .cs-field select:focus,
        .cs-field textarea:focus {
          outline: 2px solid #8E7CC3;
          outline-offset: 1px;
        }
        .cs-field input[type="file"] {
          padding: 8px 10px;
          font-size: 13px;
        }
        .cs-field textarea {
          resize: vertical;
          min-height: 90px;
        }
        .cs-section-title {
          font-size: 12px;
          font-weight: 600;
          letter-spacing: 0.08em;
          text-transform: uppercase;
          color: #2B4A3A;
          margin: 22px 0 10px 0;
        }
        .cs-section-title:first-of-type {
          margin-top: 0;
        }
        .cs-checkbox-row {
          display: flex;
          align-items: flex-start;
          gap: 8px;
          font-size: 12.5px;
          color: #3D4F47;
          grid-column: 1 / -1;
        }
        .cs-checkbox-row input {
          margin-top: 2px;
        }

        @keyframes cs-fade-in {
          from { opacity: 0; }
          to { opacity: 1; }
        }
        @keyframes cs-pop-in {
          from { opacity: 0; transform: translateY(16px) scale(0.96); }
          to { opacity: 1; transform: translateY(0) scale(1); }
        }
        .cs-modal-overlay {
          animation: cs-fade-in 0.2s ease;
          backdrop-filter: blur(6px);
          -webkit-backdrop-filter: blur(6px);
        }
        .cs-modal-panel {
          animation: cs-pop-in 0.35s cubic-bezier(0.22, 1, 0.36, 1);
          border: 1px solid rgba(30, 43, 36, 0.06);
          position: relative;
        }
        .cs-modal-panel::before {
          content: "";
          position: absolute;
          top: 0;
          left: 0;
          right: 0;
          height: 5px;
          border-radius: 20px 20px 0 0;
          background: linear-gradient(90deg, #50C878 0%, #8E7CC3 100%);
        }
        .cs-modal-close {
          background: rgba(30, 43, 36, 0.05) !important;
          width: 34px;
          height: 34px;
          border-radius: 999px !important;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: background 0.2s ease, transform 0.2s ease, color 0.2s ease;
        }
        .cs-modal-close:hover {
          background: rgba(196, 83, 74, 0.12) !important;
          color: #C4534A;
          transform: rotate(90deg);
        }
        .cs-modal-title {
          position: relative;
          padding-top: 6px;
        }
        .cs-logo-card {
          position: relative;
          padding: 28px;
          background: #FFFFFF;
          border-radius: 28px;
          border: 1px solid rgba(30, 43, 36, 0.06);
          box-shadow: 0 24px 60px rgba(30, 43, 36, 0.14), 0 2px 8px rgba(30, 43, 36, 0.06);
        }
        .cs-page-full {
          animation: cs-fade-in 0.3s ease;
        }
        .cs-field-dark label {
          color: #9FB3A8;
        }
        .cs-field-dark input,
        .cs-field-dark select,
        .cs-field-dark textarea {
          background: rgba(255, 255, 255, 0.07);
          border: 1px solid rgba(255, 255, 255, 0.18);
          color: #F4F7F5;
        }
        .cs-field-dark input::placeholder,
        .cs-field-dark textarea::placeholder {
          color: rgba(244, 247, 245, 0.4);
        }
        .cs-field-dark input:focus,
        .cs-field-dark select:focus,
        .cs-field-dark textarea:focus {
          outline: 2px solid #6FDB94;
        }
      `}</style>

      {/* rising horizon glow */}
      <div
        className="cs-horizon"
        style={{
          position: "absolute",
          bottom: 0,
          left: "50%",
          transform: risen
            ? "translate(-50%, 0)"
            : "translate(-50%, 120px)",
          opacity: risen ? 1 : 0,
          transition: "transform 2.2s cubic-bezier(0.22,1,0.36,1), opacity 2.2s ease",
          width: "140%",
          height: "60%",
          borderRadius: "50%",
          background:
            "radial-gradient(ellipse at center, rgba(80,200,120,0.22) 0%, rgba(142,124,195,0.12) 40%, rgba(20,33,26,0) 70%)",
          filter: "blur(2px)",
          pointerEvents: "none",
        }}
      />

      {/* horizon line */}
      <div
        style={{
          position: "absolute",
          bottom: "38%",
          left: 0,
          right: 0,
          height: "1px",
          background:
            "linear-gradient(90deg, rgba(111,219,148,0) 0%, rgba(111,219,148,0.25) 50%, rgba(111,219,148,0) 100%)",
        }}
      />

      {/* header / nav — full-width bar */}
      <header
        style={{
          position: "relative",
          zIndex: 3,
          width: "100%",
          background: "#1E2B24",
          borderBottom: "1px solid rgba(255,255,255,0.08)",
        }}
      >
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: "24px",
            padding: "14px 32px",
            flexWrap: "wrap",
          }}
        >
          {/* left: logo + nav */}
          <div style={{ display: "flex", alignItems: "center", gap: "40px", flexWrap: "wrap" }}>
            <img
              src={LOGO_SRC}
              alt="WebAdd logo"
              onClick={() => navigateTo("Home")}
              style={{
                height: "34px",
                width: "auto",
                objectFit: "contain",
                cursor: "pointer",
                flexShrink: 0,
                background: "#FFFFFF",
                borderRadius: "8px",
                padding: "4px 8px",
              }}
            />
            <nav
              className="cs-nav-links"
              style={{
                display: "flex",
                alignItems: "center",
                gap: "28px",
                flexWrap: "wrap",
              }}
            >
              {NAV_ITEMS.map((item) => (
                <a key={item} href="#" className="cs-nav-link" onClick={handleNavItemClick(item)}>
                  {item}
                </a>
              ))}
            </nav>
          </div>

          {/* right: search, divider, sign in, CTA */}
          <div style={{ display: "flex", alignItems: "center", gap: "18px" }}>
            <form className="cs-search-form" onSubmit={handleSearch} role="search">
              <input
                className="cs-search-input"
                type="search"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search"
              />
              <button type="submit" className="cs-search-btn" aria-label="Search">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                  <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </form>

            <div
              className="cs-nav-links"
              style={{ width: "1px", height: "24px", background: "rgba(255,255,255,0.15)" }}
            />

            <a
              href="#"
              className="cs-signin-link"
              onClick={(e) => {
                e.preventDefault();
                setShowSignin(true);
              }}
            >
              Sign in
            </a>

            <button type="button" className="cs-cta-btn" onClick={() => setShowSignup(true)}>
              Sign up
            </button>

            <button
              className="cs-burger"
              onClick={() => setMenuOpen((v) => !v)}
              aria-label="Toggle menu"
              aria-expanded={menuOpen}
              style={{
                background: "transparent",
                border: "1px solid rgba(255,255,255,0.25)",
                borderRadius: "10px",
                width: "40px",
                height: "40px",
                alignItems: "center",
                justifyContent: "center",
                cursor: "pointer",
                color: "#F4F7F5",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <line x1="3" y1="6" x2="21" y2="6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="3" y1="12" x2="21" y2="12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="3" y1="18" x2="21" y2="18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>
          </div>
        </div>

        {menuOpen && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "14px",
              background: "#1E2B24",
              borderTop: "1px solid rgba(255,255,255,0.08)",
              padding: "18px 32px 24px",
            }}
          >
            {NAV_ITEMS.map((item) => (
              <a
                key={item}
                href="#"
                className="cs-nav-link"
                style={{ fontSize: "15px" }}
                onClick={handleNavItemClick(item)}
              >
                {item}
              </a>
            ))}
            <form className="cs-search-form" onSubmit={handleSearch} role="search">
              <input
                className="cs-search-input"
                type="search"
                placeholder="Search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                aria-label="Search"
                style={{ width: "100%" }}
              />
              <button type="submit" className="cs-search-btn" aria-label="Search">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
                  <circle cx="11" cy="11" r="7" stroke="currentColor" strokeWidth="2" />
                  <line x1="16.65" y1="16.65" x2="21" y2="21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                </svg>
              </button>
            </form>
            <a
              href="#"
              className="cs-signin-link"
              onClick={(e) => {
                e.preventDefault();
                setMenuOpen(false);
                setShowSignin(true);
              }}
            >
              Sign in
            </a>
            <button
              type="button"
              className="cs-cta-btn"
              style={{ width: "fit-content" }}
              onClick={() => {
                setMenuOpen(false);
                setShowSignup(true);
              }}
            >
              Sign up
            </button>
          </div>
        )}
      </header>

      {/* content */}
      <main
        style={{
          position: "relative",
          zIndex: 2,
          flex: 1,
          display: "flex",
          flexDirection: "column",
          alignItems: "stretch",
          width: "100%",
          minHeight: "calc(100vh - 140px)",
        }}
      >
        {activeSection === "Home" ? (
          <>
            <div style={{ width: "100%", padding: "44px 24px 4px", textAlign: "center" }}>
              {submitted ? (
                <p
                  style={{
                    color: "#50C878",
                    fontSize: "15px",
                    letterSpacing: "0.02em",
                  }}
                >
                  You're on the list. We'll be in touch soon.
                </p>
              ) : (
                <>
                  <form
                    onSubmit={handleSubmit}
                    style={{
                      display: "flex",
                      gap: "10px",
                      flexWrap: "wrap",
                      justifyContent: "center",
                    }}
                  >
                    <input
                      className="cs-input"
                      type="email"
                      required
                      placeholder="you@example.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      disabled={sending}
                      style={{
                        background: "rgba(255,255,255,0.75)",
                        border: "1px solid rgba(30,43,36,0.15)",
                        borderRadius: "999px",
                        padding: "12px 20px",
                        color: "#1E2B24",
                        fontSize: "14px",
                        fontFamily: "inherit",
                        width: "260px",
                        maxWidth: "70vw",
                        opacity: sending ? 0.7 : 1,
                      }}
                    />
                    <button
                      type="submit"
                      className="cs-btn"
                      disabled={sending}
                      style={{
                        background: "#50C878",
                        color: "#0B1026",
                        border: "none",
                        borderRadius: "999px",
                        padding: "12px 26px",
                        fontSize: "14px",
                        fontWeight: 500,
                        fontFamily: "inherit",
                        cursor: sending ? "default" : "pointer",
                        opacity: sending ? 0.7 : 1,
                      }}
                    >
                      {sending ? "Sending…" : "Notify me"}
                    </button>
                  </form>
                  {error && (
                    <p style={{ marginTop: "14px", color: "#C4534A", fontSize: "13px" }}>
                      {error}
                    </p>
                  )}
                </>
              )}
            </div>

            {/* Founder message — now the full-page centerpiece of Home */}
            <div style={{ width: "100%", flex: "1 1 auto", display: "flex", flexDirection: "column", padding: "32px 0 0" }}>
              <h2
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontWeight: 400,
                  fontStyle: "italic",
                  color: "#1E2B24",
                  fontSize: "24px",
                  margin: "0 0 20px 0",
                  textAlign: "center",
                  padding: "0 24px",
                }}
              >
                {SECTION_CONTENT.Home.title}
              </h2>

              <div
                className="cs-page-full"
                style={{
                  position: "relative",
                  flex: "1 1 auto",
                  minHeight: "60vh",
                  display: "flex",
                  flexWrap: "wrap",
                  width: "100%",
                  background: "linear-gradient(160deg, #14211A 0%, #1E2B24 100%)",
                }}
              >
                <div
                  style={{
                    flex: "1 1 320px",
                    minHeight: "320px",
                  }}
                >
                  <img
                    src={SECTION_CONTENT.Home.founder.photo}
                    alt={SECTION_CONTENT.Home.founder.name}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                      display: "block",
                    }}
                  />
                </div>

                <div
                  style={{
                    position: "relative",
                    flex: "1 1 380px",
                    padding: "56px 8vw",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                  }}
                >
                  <svg
                    width="52"
                    height="40"
                    viewBox="0 0 40 30"
                    fill="none"
                    style={{ marginBottom: "16px", opacity: 0.85 }}
                  >
                    <path
                      d="M0 30V19.5C0 8.5 6 1.5 15.5 0L17 4.5C11 6 8 10 8 15H16V30H0ZM24 30V19.5C24 8.5 30 1.5 39.5 0L41 4.5C35 6 32 10 32 15H40V30H24Z"
                      fill="#6FDB94"
                    />
                  </svg>

                  <p
                    style={{
                      color: "#F4F7F5",
                      fontSize: "clamp(15px, 1.6vw, 19px)",
                      lineHeight: 1.75,
                      margin: "0 0 24px 0",
                      maxWidth: "560px",
                    }}
                  >
                    {SECTION_CONTENT.Home.founder.quote}
                  </p>

                  <p style={{ color: "#FFFFFF", fontSize: "16px", fontWeight: 600, margin: "0 0 2px 0" }}>
                    {SECTION_CONTENT.Home.founder.name}
                  </p>
                  <p style={{ color: "#9FB3A8", fontSize: "13px", margin: 0 }}>
                    {SECTION_CONTENT.Home.founder.title}
                  </p>

                  <div
                    style={{
                      height: "1px",
                      maxWidth: "300px",
                      background: "rgba(255,255,255,0.14)",
                      margin: "22px 0 0 0",
                    }}
                  />

                  <img
                    src={LOGO_SRC}
                    alt="WebAdd"
                    style={{
                      position: "absolute",
                      bottom: "24px",
                      right: "6vw",
                      height: "20px",
                      width: "auto",
                      opacity: 0.6,
                      filter: "grayscale(1) brightness(2)",
                    }}
                  />
                </div>
              </div>
            </div>
          </>
        ) : (
          <div
            style={{
              width: "100%",
              flex: "1 1 auto",
              minHeight: "calc(100vh - 140px)",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
              padding: "64px 6vw",
              textAlign: "left",
              background: "linear-gradient(160deg, #14211A 0%, #1E2B24 100%)",
            }}
          >
            <div style={{ width: "100%", maxWidth: "760px" }} className="cs-page-full">
              <a
                href="/"
                onClick={(e) => {
                  e.preventDefault();
                  navigateTo("Home");
                }}
                style={{
                  display: "inline-block",
                  marginBottom: "20px",
                  color: "#9FB3A8",
                  fontSize: "13px",
                  textDecoration: "none",
                }}
              >
                ← Back to home
              </a>

              <svg
                width="44"
                height="34"
                viewBox="0 0 40 30"
                fill="none"
                style={{ marginBottom: "14px", opacity: 0.85 }}
              >
                <path
                  d="M0 30V19.5C0 8.5 6 1.5 15.5 0L17 4.5C11 6 8 10 8 15H16V30H0ZM24 30V19.5C24 8.5 30 1.5 39.5 0L41 4.5C35 6 32 10 32 15H40V30H24Z"
                  fill="#6FDB94"
                />
              </svg>

              <h2
                style={{
                  fontFamily: "'Fraunces', serif",
                  fontWeight: 400,
                  fontStyle: "italic",
                  color: "#FFFFFF",
                  fontSize: "32px",
                  margin: "0 0 18px 0",
                }}
              >
                {SECTION_CONTENT[activeSection].title}
              </h2>

              {SECTION_CONTENT[activeSection].paragraphs?.map((p, i) => (
                <p key={i} style={{ color: "#D8E3DC", fontSize: "15.5px", lineHeight: 1.7, margin: "0 0 14px 0" }}>
                  {p}
                </p>
              ))}

              {SECTION_CONTENT[activeSection].list && (
                <ul style={{ margin: "0 0 14px 0", paddingLeft: "20px", color: "#D8E3DC", fontSize: "15.5px", lineHeight: 1.8 }}>
                  {SECTION_CONTENT[activeSection].list.map((item, i) => (
                    <li key={i}>{item}</li>
                  ))}
                </ul>
              )}

              {SECTION_CONTENT[activeSection].quotes && (
                <div style={{ display: "flex", flexDirection: "column", gap: "16px", margin: "6px 0 14px 0" }}>
                  {SECTION_CONTENT[activeSection].quotes.map((q, i) => (
                    <div
                      key={i}
                      style={{
                        background: "rgba(255,255,255,0.06)",
                        borderLeft: "3px solid #6FDB94",
                        borderRadius: "8px",
                        padding: "14px 18px",
                      }}
                    >
                      <p style={{ margin: "0 0 6px 0", color: "#F4F7F5", fontSize: "15px", fontStyle: "italic" }}>
                        “{q.text}”
                      </p>
                      <p style={{ margin: 0, color: "#9FB3A8", fontSize: "12.5px" }}>— {q.author}</p>
                    </div>
                  ))}
                </div>
              )}

              {SECTION_CONTENT[activeSection].footnote && (
                <p style={{ color: "#9FB3A8", fontSize: "13.5px", margin: "6px 0 0 0" }}>
                  {SECTION_CONTENT[activeSection].footnote}
                </p>
              )}

              {SECTION_CONTENT[activeSection].cta === "connect" && (
                <button
                  type="button"
                  className="cs-btn"
                  onClick={() => navigateTo("Connect")}
                  style={{
                    background: "#50C878",
                    color: "#0B1026",
                    border: "none",
                    borderRadius: "999px",
                    padding: "13px 28px",
                    fontSize: "14.5px",
                    fontWeight: 400,
                    fontFamily: "inherit",
                    cursor: "pointer",
                    marginTop: "22px",
                  }}
                >
                  Send us a message
                </button>
              )}

              {SECTION_CONTENT[activeSection].isConnectForm && (
                connectDone ? (
                  <p style={{ color: "#6FDB94", fontSize: "15px", marginTop: "8px" }}>
                    Thanks, {connectName || "there"}! We've received your message and will reach out soon.
                  </p>
                ) : (
                  <form onSubmit={handleConnectSubmit} style={{ display: "flex", flexDirection: "column", gap: "12px", maxWidth: "420px" }}>
                    <div className="cs-field cs-field-dark">
                      <label htmlFor="connectName">Name *</label>
                      <input
                        id="connectName"
                        type="text"
                        required
                        value={connectName}
                        onChange={(e) => setConnectName(e.target.value)}
                        disabled={connectSending}
                        autoFocus
                      />
                    </div>
                    <div className="cs-field cs-field-dark">
                      <label htmlFor="connectEmail">Email *</label>
                      <input
                        id="connectEmail"
                        type="email"
                        required
                        value={connectEmail}
                        onChange={(e) => setConnectEmail(e.target.value)}
                        disabled={connectSending}
                      />
                    </div>
                    <div className="cs-field cs-field-dark">
                      <label htmlFor="connectMobile">Mobile number</label>
                      <input
                        id="connectMobile"
                        type="tel"
                        value={connectMobile}
                        onChange={(e) => setConnectMobile(e.target.value)}
                        disabled={connectSending}
                      />
                    </div>
                    <div className="cs-field cs-field-dark">
                      <label htmlFor="connectMessage">Message *</label>
                      <textarea
                        id="connectMessage"
                        required
                        value={connectMessage}
                        onChange={(e) => setConnectMessage(e.target.value)}
                        disabled={connectSending}
                      />
                    </div>

                    <button
                      type="submit"
                      className="cs-btn"
                      disabled={connectSending}
                      style={{
                        background: "#50C878",
                        color: "#0B1026",
                        border: "none",
                        borderRadius: "999px",
                        padding: "12px 26px",
                        fontSize: "14.5px",
                        fontWeight: 400,
                        fontFamily: "inherit",
                        cursor: connectSending ? "default" : "pointer",
                        opacity: connectSending ? 0.7 : 1,
                        marginTop: "6px",
                      }}
                    >
                      {connectSending ? "Sending…" : "Send message"}
                    </button>

                    {connectError && (
                      <p style={{ color: "#C4534A", fontSize: "13px", margin: 0 }}>{connectError}</p>
                    )}
                  </form>
                )
              )}
            </div>
          </div>
        )}
      </main>

      <footer
        style={{
          position: "relative",
          zIndex: 2,
          textAlign: "center",
          color: "#9FB3A8",
          fontSize: "12px",
          padding: "20px",
          letterSpacing: "0.04em",
        }}
      >
        <p style={{ margin: "0 0 6px 0" }}>Your next step starts here.</p>
        <p style={{ margin: 0 }}>© {new Date().getFullYear()} WebAdd. All rights reserved.</p>
      </footer>

      {showSignup && (
        <div
          onClick={closeSignup}
          className="cs-modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10,
            background: "rgba(20,35,27,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="cs-modal-panel"
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "640px",
              maxHeight: "88vh",
              overflowY: "auto",
              background: "#FCFAF6",
              borderRadius: "20px",
              padding: "36px 32px",
              boxShadow: "0 20px 60px rgba(20,35,27,0.25)",
            }}
          >
            <button
              onClick={closeSignup}
              aria-label="Close sign up"
              className="cs-modal-close"
              style={{
                position: "sticky",
                top: 0,
                float: "right",
                marginTop: "-12px",
                marginRight: "-12px",
                border: "none",
                cursor: "pointer",
                color: "#5E7368",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <line x1="5" y1="5" x2="19" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="19" y1="5" x2="5" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>

            <h2
              className="cs-modal-title"
              style={{
                fontFamily: "'Fraunces', serif",
                fontWeight: 400,
                fontStyle: "italic",
                color: "#1E2B24",
                fontSize: "28px",
                margin: "0 0 6px 0",
              }}
            >
              Create your account
            </h2>
            <p style={{ color: "#5E7368", fontSize: "14px", margin: "0 0 8px 0" }}>
              Be first through the door when we launch.
            </p>

            {signupDone ? (
              <p style={{ color: "#50C878", fontSize: "15px", marginTop: "20px" }}>
                You're all set, {formData.firstName || "there"}. We'll email you soon.
              </p>
            ) : (
              <form onSubmit={handleSignup}>
                <p className="cs-section-title">Personal details</p>
                <div className="cs-form-grid">
                  <div className="cs-field">
                    <label htmlFor="firstName">First name *</label>
                    <input id="firstName" name="firstName" type="text" required
                      value={formData.firstName} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="lastName">Last name *</label>
                    <input id="lastName" name="lastName" type="text" required
                      value={formData.lastName} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="parentName">Father's / Mother's name</label>
                    <input id="parentName" name="parentName" type="text"
                      value={formData.parentName} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="dob">Date of birth</label>
                    <input id="dob" name="dob" type="date"
                      value={formData.dob} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="gender">Gender</label>
                    <select id="gender" name="gender" value={formData.gender} onChange={handleFieldChange} disabled={signupSending}>
                      <option>Male</option>
                      <option>Female</option>
                      <option>Other</option>
                    </select>
                  </div>
                </div>

                <p className="cs-section-title">Contact details</p>
                <div className="cs-form-grid">
                  <div className="cs-field">
                    <label htmlFor="mobile">Mobile number *</label>
                    <input id="mobile" name="mobile" type="tel" required
                      value={formData.mobile} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="altMobile">Alternate mobile</label>
                    <input id="altMobile" name="altMobile" type="tel"
                      value={formData.altMobile} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field cs-field-full">
                    <label htmlFor="email">Email address *</label>
                    <input id="email" name="email" type="email" required
                      value={formData.email} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                </div>

                <p className="cs-section-title">Address</p>
                <div className="cs-form-grid">
                  <div className="cs-field">
                    <label htmlFor="house">House / Flat no.</label>
                    <input id="house" name="house" type="text"
                      value={formData.house} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="street">Street</label>
                    <input id="street" name="street" type="text"
                      value={formData.street} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="city">City</label>
                    <input id="city" name="city" type="text"
                      value={formData.city} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="district">District</label>
                    <input id="district" name="district" type="text"
                      value={formData.district} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="state">State</label>
                    <select id="state" name="state" value={formData.state} onChange={handleFieldChange} disabled={signupSending}>
                      <option value="">Select state</option>
                      {INDIAN_STATES.map((s) => (
                        <option key={s} value={s}>{s}</option>
                      ))}
                    </select>
                  </div>
                  <div className="cs-field">
                    <label htmlFor="pin">PIN code</label>
                    <input id="pin" name="pin" type="text" maxLength={6} pattern="[0-9]{6}"
                      value={formData.pin} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field cs-field-full">
                    <label htmlFor="country">Country</label>
                    <input id="country" name="country" type="text"
                      value={formData.country} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                </div>

                <p className="cs-section-title">Identity verification</p>
                <div className="cs-form-grid">
                  <div className="cs-field">
                    <label htmlFor="aadhaar">Aadhaar number</label>
                    <input id="aadhaar" name="aadhaar" type="text" maxLength={12} pattern="[0-9]{12}"
                      value={formData.aadhaar} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="pan">PAN number</label>
                    <input id="pan" name="pan" type="text" maxLength={10} style={{ textTransform: "uppercase" }}
                      value={formData.pan} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="profilePhoto">Profile photo</label>
                    <input id="profilePhoto" name="profilePhoto" type="file" accept="image/*"
                      onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="identityProof">Identity proof</label>
                    <input id="identityProof" name="identityProof" type="file" accept="image/*,application/pdf"
                      onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                </div>

                <p className="cs-section-title">Account setup</p>
                <div className="cs-form-grid">
                  <div className="cs-field cs-field-full">
                    <label htmlFor="username">Username *</label>
                    <input id="username" name="username" type="text" required
                      value={formData.username} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="password">Password *</label>
                    <input id="password" name="password" type="password" required
                      value={formData.password} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="confirmPassword">Confirm password *</label>
                    <input id="confirmPassword" name="confirmPassword" type="password" required
                      value={formData.confirmPassword} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="securityQuestion">Security question</label>
                    <select id="securityQuestion" name="securityQuestion" value={formData.securityQuestion} onChange={handleFieldChange} disabled={signupSending}>
                      <option value="">Select a question</option>
                      {SECURITY_QUESTIONS.map((q) => (
                        <option key={q} value={q}>{q}</option>
                      ))}
                    </select>
                  </div>
                  <div className="cs-field">
                    <label htmlFor="securityAnswer">Answer</label>
                    <input id="securityAnswer" name="securityAnswer" type="text"
                      value={formData.securityAnswer} onChange={handleFieldChange} disabled={signupSending} />
                  </div>
                </div>

                <p className="cs-section-title">Preferences</p>
                <div className="cs-form-grid">
                  <div className="cs-field">
                    <label htmlFor="preferredLanguage">Preferred language</label>
                    <select id="preferredLanguage" name="preferredLanguage" value={formData.preferredLanguage} onChange={handleFieldChange} disabled={signupSending}>
                      {LANGUAGES.map((l) => (
                        <option key={l} value={l}>{l}</option>
                      ))}
                    </select>
                  </div>
                  <div className="cs-field">
                    <label htmlFor="preferredCommunication">Preferred communication</label>
                    <select id="preferredCommunication" name="preferredCommunication" value={formData.preferredCommunication} onChange={handleFieldChange} disabled={signupSending}>
                      {COMMUNICATION_MODES.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <p className="cs-section-title">Agreements</p>
                <div className="cs-form-grid">
                  <label className="cs-checkbox-row">
                    <input type="checkbox" name="termsAgreed" checked={formData.termsAgreed} onChange={handleFieldChange} disabled={signupSending} />
                    I agree to the Terms and Conditions *
                  </label>
                  <label className="cs-checkbox-row">
                    <input type="checkbox" name="privacyAgreed" checked={formData.privacyAgreed} onChange={handleFieldChange} disabled={signupSending} />
                    I agree to the Privacy Policy *
                  </label>
                  <label className="cs-checkbox-row">
                    <input type="checkbox" name="captchaChecked" checked={formData.captchaChecked} onChange={handleFieldChange} disabled={signupSending} />
                    I'm not a robot
                  </label>
                </div>

                <button
                  type="submit"
                  className="cs-btn"
                  disabled={signupSending}
                  style={{
                    background: "#50C878",
                    color: "#0B1026",
                    border: "none",
                    borderRadius: "999px",
                    padding: "12px 26px",
                    fontSize: "14.5px",
                    fontWeight: 400,
                    fontFamily: "inherit",
                    cursor: signupSending ? "default" : "pointer",
                    opacity: signupSending ? 0.7 : 1,
                    marginTop: "22px",
                    width: "100%",
                  }}
                >
                  {signupSending ? "Creating account…" : "Create account"}
                </button>

                {signupError && (
                  <p style={{ color: "#C4534A", fontSize: "13px", margin: "10px 0 0 0" }}>{signupError}</p>
                )}

                <p style={{ color: "#7C9088", fontSize: "12.5px", textAlign: "center", margin: "14px 0 0 0" }}>
                  Already have an account?{" "}
                  <a
                    href="#"
                    className="cs-signin-link"
                    style={{ fontSize: "12.5px" }}
                    onClick={(e) => {
                      e.preventDefault();
                      openSignin();
                    }}
                  >
                    Sign in
                  </a>
                </p>
              </form>
            )}
          </div>
        </div>
      )}

      {showSignin && (
        <div
          onClick={closeSignin}
          className="cs-modal-overlay"
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 10,
            background: "rgba(20,35,27,0.45)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: "20px",
          }}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            className="cs-modal-panel"
            style={{
              position: "relative",
              width: "100%",
              maxWidth: "400px",
              background: "#FCFAF6",
              borderRadius: "20px",
              padding: "36px 32px",
              boxShadow: "0 20px 60px rgba(20,35,27,0.25)",
            }}
          >
            <button
              onClick={closeSignin}
              aria-label="Close sign in"
              className="cs-modal-close"
              style={{
                position: "absolute",
                top: "16px",
                right: "16px",
                border: "none",
                cursor: "pointer",
                color: "#5E7368",
              }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                <line x1="5" y1="5" x2="19" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
                <line x1="19" y1="5" x2="5" y2="19" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
              </svg>
            </button>

            <h2
              className="cs-modal-title"
              style={{
                fontFamily: "'Fraunces', serif",
                fontWeight: 400,
                fontStyle: "italic",
                color: "#1E2B24",
                fontSize: "28px",
                margin: "0 0 6px 0",
              }}
            >
              Sign in
            </h2>

            {signinStep === "credentials" && (
              <>
                <p style={{ color: "#5E7368", fontSize: "14px", margin: "0 0 24px 0" }}>
                  Enter your username or mobile number, and your password.
                </p>
                <form onSubmit={handleSigninCredentials} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div className="cs-field">
                    <label htmlFor="signinUsername">Username</label>
                    <input
                      id="signinUsername"
                      type="text"
                      value={signinUsername}
                      onChange={(e) => setSigninUsername(e.target.value)}
                      disabled={signinSending}
                      autoFocus
                    />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="signinMobile">Mobile number</label>
                    <input
                      id="signinMobile"
                      type="tel"
                      placeholder="e.g. 98765 43210"
                      value={signinMobile}
                      onChange={(e) => setSigninMobile(e.target.value)}
                      disabled={signinSending}
                    />
                  </div>
                  <div className="cs-field">
                    <label htmlFor="signinPassword">Password</label>
                    <input
                      id="signinPassword"
                      type="password"
                      required
                      value={signinPassword}
                      onChange={(e) => setSigninPassword(e.target.value)}
                      disabled={signinSending}
                    />
                  </div>

                  <button
                    type="submit"
                    className="cs-btn"
                    disabled={signinSending}
                    style={{
                      background: "#50C878",
                      color: "#0B1026",
                      border: "none",
                      borderRadius: "999px",
                      padding: "12px 26px",
                      fontSize: "14.5px",
                      fontWeight: 400,
                      fontFamily: "inherit",
                      cursor: signinSending ? "default" : "pointer",
                      opacity: signinSending ? 0.7 : 1,
                      marginTop: "6px",
                    }}
                  >
                    {signinSending ? "Checking…" : "Send OTP"}
                  </button>

                  {signinError && (
                    <p style={{ color: "#C4534A", fontSize: "13px", margin: 0 }}>{signinError}</p>
                  )}

                  <p style={{ color: "#7C9088", fontSize: "12.5px", textAlign: "center", margin: "6px 0 0 0" }}>
                    New here?{" "}
                    <a
                      href="#"
                      className="cs-signin-link"
                      style={{ fontSize: "12.5px" }}
                      onClick={(e) => {
                        e.preventDefault();
                        setShowSignin(false);
                        setShowSignup(true);
                      }}
                    >
                      Create an account
                    </a>
                  </p>
                </form>
              </>
            )}

            {signinStep === "otp" && (
              <>
                <p style={{ color: "#5E7368", fontSize: "14px", margin: "0 0 24px 0" }}>
                  We sent a one-time code to {otpMaskedContact}. Enter it below to sign in.
                </p>
                <form onSubmit={handleVerifyOtp} style={{ display: "flex", flexDirection: "column", gap: "12px" }}>
                  <div className="cs-field">
                    <label htmlFor="signinOtp">One-time password</label>
                    <input
                      id="signinOtp"
                      type="text"
                      inputMode="numeric"
                      maxLength={6}
                      required
                      value={signinOtp}
                      onChange={(e) => setSigninOtp(e.target.value.replace(/\D/g, ""))}
                      disabled={signinSending}
                      autoFocus
                      style={{ letterSpacing: "0.3em", textAlign: "center", fontSize: "18px" }}
                    />
                  </div>

                  <button
                    type="submit"
                    className="cs-btn"
                    disabled={signinSending}
                    style={{
                      background: "#50C878",
                      color: "#0B1026",
                      border: "none",
                      borderRadius: "999px",
                      padding: "12px 26px",
                      fontSize: "14.5px",
                      fontWeight: 400,
                      fontFamily: "inherit",
                      cursor: signinSending ? "default" : "pointer",
                      opacity: signinSending ? 0.7 : 1,
                      marginTop: "6px",
                    }}
                  >
                    {signinSending ? "Verifying…" : "Verify & sign in"}
                  </button>

                  {signinError && (
                    <p style={{ color: "#C4534A", fontSize: "13px", margin: 0 }}>{signinError}</p>
                  )}

                  <p style={{ color: "#7C9088", fontSize: "12.5px", textAlign: "center", margin: "6px 0 0 0" }}>
                    {resendCooldown > 0 ? (
                      <>Resend OTP in {resendCooldown}s</>
                    ) : (
                      <a
                        href="#"
                        className="cs-signin-link"
                        style={{ fontSize: "12.5px" }}
                        onClick={(e) => {
                          e.preventDefault();
                          handleResendOtp();
                        }}
                      >
                        Resend OTP
                      </a>
                    )}
                    {"  ·  "}
                    <a
                      href="#"
                      className="cs-signin-link"
                      style={{ fontSize: "12.5px" }}
                      onClick={(e) => {
                        e.preventDefault();
                        setSigninStep("credentials");
                        setSigninOtp("");
                        setSigninError("");
                      }}
                    >
                      Change username
                    </a>
                  </p>
                </form>
              </>
            )}

            {signinStep === "done" && (
              <p style={{ color: "#50C878", fontSize: "15px", marginTop: "8px" }}>
                You're signed in as <strong>{signinUsername}</strong>.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
